<template lang="pug">
#reservation-detail
  //- title
  .d-flex.flex-row.justify-content-center.mb-1(v-if="reservation")
    .page-title
      .d-flex.align-items-center.mr-0(v-if="reservation.type == 0")
        .icon-offline.mr-1
        span 訪問面会予約詳細
      .d-flex.align-items-center.mr-0(v-else-if="reservation.type == 1")
        .icon-online.mr-1
        span.mt-1 オンライン面会予約詳細

  .mb-3.separator(v-if="reservation")

  .content(v-if="reservation")
    //- status
    .mb-0
      span.key-name 状態
    .mb-3
      span.status-no-consented(v-if="reservation.consented == false") 未承諾（{{ _inputType }}）
      span.status-consented(v-else) 承諾済（{{ _inputType }}）

    //- datetime
    .mb-0
      span.key-name 面会日時
    .mb-3
      span.date {{ getDate(reservation) }}
      span （
      span.week(:class="weekCssClass(reservation)") {{ getWeek(reservation) }}
      span ）
      span.time {{ getTime(reservation) }}
    
    //- resident
    .mb-0
      span.key-name 利用者名（部屋名）
    .mb-3
      span {{ reservation.residentName }}（{{ reservation.residentRoom }}）

    //- family
    .mb-0
      span.key-name 面会者名（続柄・電話番号）
    .mb-3
      span {{ reservation.familyName }}（{{ reservation.familyRelation }}・{{ reservation.familyTel }}）

    //- text
    .mb-0
      span.key-name 家族からの連絡事項
      .pen(@click="showEditTextInput = !showEditTextInput")
    .mb-3
      span.reservation-text(v-if="!showEditTextInput") {{ reservation.text }}
      .reservation-text-edit(v-else)
        .d-flex.flex-column
          b-form-textarea(v-model="editText" placeholder="家族からの連絡事項" rows="6" max-rows="6" maxlength="1000"
            v-validate="{ max: 1000 }" data-vv-as="家族からの連絡事項" @focus="hideFooter" @blur="showFooter")
          .mb-2
          .buttons
            b-button.btn.mr-2(@click="onUpdateReservationText") 更新
            b-button.btn(@click="onUpdateReservationCancel") キャンセル

    //- controll
    .mb-0
      span.key-name 予約・面会の操作
    .mb-4
      template(v-if="reservation.consented == false")
        template(v-if="_useEditReservations")
          b-button.btn.back-btn.mr-2(@click="onConsented(reservation)") 予約承諾
          b-button.btn.back-btn.mr-2(@click="onUnconsented(reservation)") 予約辞退
        template(v-else)
          b-button.btn.back-btn.mr-2.disable(disabled) 予約承諾
          b-button.btn.back-btn.mr-2.disable(disabled) 予約辞退
      template(v-else)
        template(v-if="_useEditReservations")
          b-button.btn.back-btn.mr-2(@click="onConsentedCancel(reservation)") 予約キャンセル
        template(v-else)
          b-button.btn.back-btn.mr-2.disable(disabled) 予約キャンセル
      template(v-if="reservation.type == 1 && reservation.consented == true")
        template(v-if="_useStartOnlineReservations")
          b-button.btn.back-btn.mr-2(@click="onOnlineUrl(reservation)") 面会開始
        template(v-else)
          b-button.btn.back-btn.mr-2.disable(disabled) 面会開始

    //- error message
    .mb-4(v-if="errorMessage != ''")
      span.error-message {{ errorMessage }}

    //- buttons
    .d-flex.flex-row.justify-content-center
      b-button.btn.back-btn(@click="onBack") 戻る

  //- loading modal
  Loading(:isBusy="isBusy" :message="loadingMessage")

  //- consented modals
  OkCancelModal(v-model="showConsentedModal" :title="consentedModalTitle" @ok="onConsentedModalOk")
  OkModal(v-model="showConsentedSuccessModal" :title="consentedSuccessModalTitle" @ok="onConsentedSuccessModalOk")

  //- unconsented modals
  MessageModal(v-model="showUnconsentedRegisterModal" :modalTitle="messageModalTitle" :modalMessage="messageModalMessage"
    :title.sync="messageTitle" :text.sync="messageText" @onSend="confirmUnconsented")
  MessageConfirmModal(v-model="showUnconsentedRegisterConfirmModal" :modalTitle="messageConfirmModalTitle" :title="messageTitle" :text="messageText"
    @onSend="unconsented")
  OkModal(v-model="showOkModal" :title="okModalTitle" @ok="onBack")

  //- cancel modals
  OkCancelModal(v-model="showConsentedCancelRegisterModalByNoMessage" :title="modalMessage" :message="modalOptionMessage" @ok="consentedCancel")
  MessageModal(v-model="showConsentedCancelRegisterModal" :modalTitle="messageModalTitle" :modalMessage="messageModalMessage"
    :title.sync="messageTitle" :text.sync="messageText" @onSend="confirmConsentedCancel")
  MessageConfirmModal(v-model="showConsentedCancelRegisterConfirmModal" :modalTitle="messageConfirmModalTitle" :title="messageTitle" :text="messageText"
    @onSend="consentedCancel")
  OkModal(v-model="showOkModal" :title="okModalTitle" @ok="onBack")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import SystemError from "@/commons/models/SystemError";
import ReservationsService, {
  InputType,
  ReservationDeleteSendType,
  ReservationsDeleteModel,
  ReservationsGetModel,
  ReservationsConsentedPatchModel,
  ReservationsPatchModel
} from "@/commons/services/Reservations";
import FamiliesService, { FamilyGetModel, SenderType } from "@/commons/services/Families";

import Loading from "@/mobile/basics/Loading.vue";
import OkCancelModal from "@/mobile/basics/OkCancelModal.vue";
import OkModal from "@/mobile/basics/OkModal.vue";
import { ReservationType } from "@/commons/services/Reservations";
import MessageModal from "@/mobile/components/modals/MessageModal.vue";
import MessageConfirmModal from "@/mobile/components/modals/MessageConfirmModal.vue";

/**
 * ### 面会予約詳細コンポーネント
 */
@Component({
  components: {
    Loading,
    OkCancelModal,
    OkModal,
    MessageModal,
    MessageConfirmModal
  }
})
export default class ReservationDetail extends Vue {
  /** 面会予約サービス */
  @Inject("ReservationsService") reservationsService!: ReservationsService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;

  /** ローディングモーダル表示可否 */
  isBusy: boolean = false;
  /** ローディングモーダルメッセージ */
  loadingMessage: string = "";

  /** エラーメッセージ */
  errorMessage: string = "";

  /** 面会予約 */
  reservation: ReservationsGetModel | null = null;
  /** 面会予約をした家族 */
  family: FamilyGetModel | null = null;

  /** 連絡事項編集の表示可否 */
  showEditTextInput: boolean = false;
  /** 編集用連絡事項 */
  editText: string = "";

  /** 承諾確認モーダル表示可否 */
  showConsentedModal: boolean = false;
  /** 承諾確認モーダルタイトル */
  consentedModalTitle: string = "";
  /** 承諾完了モーダル表示可否 */
  showConsentedSuccessModal: boolean = false;
  /** 承諾完了モーダルタイトル */
  consentedSuccessModalTitle: string = "";

  /** 辞退メッセージ入力モーダル表示可否 */
  showUnconsentedRegisterModal: boolean = false;
  /** 辞退メッセージ確認モーダル表示可否 */
  showUnconsentedRegisterConfirmModal: boolean = false;
  /** 辞退完了モーダル表示可否 */
  showUnconsentedSuccessModal: boolean = false;

  /** キャンセル確認モーダル表示可否 */
  showConsentedCancelRegisterModalByNoMessage: boolean = false;
  /** キャンセルメッセージ入力モーダル表示可否 */
  showConsentedCancelRegisterModal: boolean = false;
  /** キャンセルメッセージ確認モーダル表示可否 */
  showConsentedCancelRegisterConfirmModal: boolean = false;
  /** キャンセル完了モーダル表示可否 */
  showConsentedCancelSuccessModal: boolean = false;

  /** メッセージ入力モーダルのタイトル */
  messageModalTitle: string = "";
  /** メッセージ入力モーダルのメッセージ */
  messageModalMessage: string = "";
  /** メッセージタイトル */
  messageTitle: string = "";
  /** メッセージ本文 */
  messageText: string = "";
  /** メッセージ入力確認モーダルのタイトル */
  messageConfirmModalTitle: string = "";
  /** 単一モーダル表示のメッセージ */
  modalMessage: string = "";
  /** 単一モーダルオプションメッセージ */
  modalOptionMessage: string = "";
  /** 辞退、キャンセル完了モーダル表示可否 */
  showOkModal: boolean = false;
  /** 辞退、キャンセル完了モーダルタイトル */
  okModalTitle: string = "";

  get _inputType(): string {
    if (this.reservation && this.reservation.inputType == 0) {
      return "代理予約";
    } else {
      return "本人予約";
    }
  }

  get _useEditReservations(): boolean {
    return userStore.user.permissions.reservationsFunction.useEditReservations;
  }

  get _useStartOnlineReservations(): boolean {
    return userStore.user.permissions.reservationsFunction.useStartOnlineReservations;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報を作成する
   */
  public async createView(): Promise<void> {
    this.loadingMessage = "読込み中";
    this.isBusy = true;

    const id: string = this.$route.params.id;
    const reservation: ReservationsGetModel = await this.getReservation(Number(id));
    this.reservation = reservation;

    this.editText = this.reservation.text ? this.reservation.text : "";

    this.isBusy = false;
  }

  /**
   * ### 面会予約を取得する
   */
  public async getReservation(reservationId: number): Promise<ReservationsGetModel> {
    const response: ReservationsGetModel = await this.reservationsService.get(userStore, reservationId);
    return response;
  }

  /**
   * ### 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(reservation: ReservationsGetModel): string {
    if (reservation.datetime) {
      return this.$dateUtil.convertNumberToString(Number(reservation.datetime), "mm月dd日", false);
    } else {
      return "";
    }
  }

  /**
   * ### 曜日を返す
   */
  public getWeek(reservation: ReservationsGetModel): string {
    if (reservation.datetime) {
      const dateObject: Date = this.$dateUtil.convertNumberToDate(Number(reservation.datetime));
      return this.$dateUtil.getWeekName(dateObject, "ja");
    } else {
      return "";
    }
  }

  /**
   * ### 時分を返す
   */
  public getTime(reservation: ReservationsGetModel): string {
    if (reservation.datetime) {
      return this.$dateUtil.convertNumberToString(Number(reservation.datetime), "hh:MM");
    } else {
      return "";
    }
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(reservation: ReservationsGetModel): string {
    if (reservation.datetime) {
      let weekClassName: string = "";
      const weekName: string = this.getWeek(reservation);
      switch (weekName) {
        case "日":
          weekClassName = "sunday";
          break;
        case "土":
          weekClassName = "saturday";
          break;
        default:
          break;
      }
      return weekClassName;
    } else {
      return "";
    }
  }

  /**
   * ### 連絡事項を更新する
   */
  public async onUpdateReservationText(): Promise<void> {
    this.loadingMessage = "更新中";
    this.isBusy = true;

    try {
      const reservation: ReservationsGetModel = this.reservation as ReservationsGetModel;
      const requestBody: ReservationsPatchModel = {
        text: this.editText ? this.editText : "",
        updated: Number(reservation.updated)
      };
      await this.reservationsService.patch(userStore, reservation.id, requestBody);

      this.showEditTextInput = !this.showEditTextInput;
      this.isBusy = false;

      await this.createView();
    } catch (error) {
      const systemError: SystemError = error as SystemError;

      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw systemError;
      }
    }

    return;
  }

  /**
   * ### 連絡事項を初期化してテキスト入力を消す
   */
  public onUpdateReservationCancel(): void {
    const reservation: ReservationsGetModel = this.reservation as ReservationsGetModel;
    this.editText = reservation.text ? reservation.text : "";
    this.showEditTextInput = !this.showEditTextInput;
    return;
  }

  /**
   * ### 承諾確認モーダルを表示する
   */
  public onConsented(reservation: ReservationsGetModel): void {
    const message: string = `${reservation.residentName}（${reservation.residentRoom}）様の${reservation.familyName}（${reservation.familyRelation}）の面会予約を承諾しますか？`;
    this.consentedModalTitle = message;
    this.showConsentedModal = true;

    return;
  }

  /**
   * ### 面会予約を承諾する
   */
  public async onConsentedModalOk(): Promise<void> {
    this.loadingMessage = "承諾中";
    this.isBusy = true;

    try {
      if (this.reservation) {
        // 登録用情報を作成し、APIにリクエストを送る
        const requestBody: ReservationsConsentedPatchModel = {
          consented: true,
          consentedUserId: userStore.loginUser.userId,
          consentedUserName: userStore.loginUser.name,
          updated: Number(this.reservation.updated)
        };
        await this.reservationsService.patchConsented(userStore, this.reservation.id, requestBody);
      }

      this.consentedSuccessModalTitle = "承諾しました。";
      this.showConsentedSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 承諾完了モーダルのOKボタン押下イベント
   */
  public async onConsentedSuccessModalOk(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 辞退確認モーダルを表示する
   */
  public async onUnconsented(reservation: ReservationsGetModel): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読み込み中...";

    const family: FamilyGetModel = await this.getFamily(reservation.residentId, reservation.familyId);
    this.family = family;
    this.setMessageModalTitleAndText(family, reservation, "辞退");

    this.isBusy = false;
    this.showUnconsentedRegisterModal = true;

    return;
  }

  /**
   * ### 家族を取得する
   *
   * - 家族単一を取得するAPIはないので、利用者にひもづく家族を取得する
   * - 家族の中から家族IDが同一のものを修正対象の家族と判断する
   * - 見つからなかった場合は404画面に遷移させたいので例外を投げる
   */
  public async getFamily(residentId: number, familyId: number): Promise<FamilyGetModel> {
    const families: Array<FamilyGetModel> = await this.familiesService.getsInResident(userStore, residentId);

    for (const family of families) {
      if (family.familyId == familyId) {
        return family;
      }
    }

    let systemError: SystemError = new SystemError(`family is notfound. residentId: ${residentId}, familyId: ${familyId}.`);
    systemError.statusCode = 404;
    throw systemError;
  }

  /**
   * ### メッセージモーダルで表示する情報を格納する
   *
   * - モーダルのタイトル
   * - モーダルのメッセージ(説明文)
   * - メッセージのタイトル
   * - メッセージ本文
   *   - 送信対象の家族がLINEかメールかで本文の内容が異なる
   */
  public setMessageModalTitleAndText(family: FamilyGetModel, reservation: ReservationsGetModel, operation: string): void {
    // 共通で使用する文字列
    const reservationType: string = reservation.type == ReservationType.VISIT ? "訪問面会" : "オンライン面会";
    const reservationDatetime: string = this.$dateUtil.convertNumberToString(reservation.datetime as number, "mm月dd日 hh:MM");

    // モーダルタイトル
    this.messageModalTitle = `${reservation.residentName}（${reservation.residentRoom}）様の`;
    this.messageModalTitle += `${reservation.familyName}（${reservation.familyRelation}）の${reservationType}を${operation}しますか？`;

    // モーダルメッセージ(説明文)
    this.messageModalMessage = "ご家族へのメッセージ内容を編集して送信してください。\n送信すると面会予約が取り消されます。";

    // タイトル
    this.messageTitle = `${reservationType}の${operation}（${reservationDatetime}）`;

    // 本文の作成
    if (family.senderType == SenderType.LINE) {
      // LINEメッセージ送信
      this.messageText = `${userStore.facility.name}  ${family.name}様\n`;
      this.messageText += `${reservationDatetime}の${reservationType}の予約が施設のご都合により${operation}されました。\n\n`;
      this.messageText += `別の日程を選択して再度予約をお願いします。`;
    } else if (family.senderType == SenderType.MAIL) {
      // メール送信
      this.messageText = `HitomeQ コネクト のご家族向けメール配信サービスです。\n\n`;
      this.messageText += `${userStore.facility.name}のご都合により、${reservationType}の予約が${operation}されました。\n`;
      this.messageText += `予約日時: ${reservationDatetime}\n\n`;
      this.messageText += `別日程での再予約をご希望の場合は、${userStore.facility.name}へご連絡ください。\n\n\n`;
      this.messageText += `このメールへの返信はできませんのでご了承ください。\n`;
      this.messageText += `配信停止をご希望の場合は、ご利用の施設までご連絡ください。`;
    }

    return;
  }

  /**
   * ### 辞退確認ダイアログを表示する
   */
  public confirmUnconsented(): void {
    const reservation: ReservationsGetModel = this.reservation as ReservationsGetModel;
    this.messageConfirmModalTitle = `${reservation.residentName}（${reservation.residentRoom}）様の${reservation.familyName}（${reservation.familyRelation}）に以下の内容でメッセージを送信します。よろしいですか？`;
    this.showUnconsentedRegisterConfirmModal = true;
    return;
  }

  /**
   * ### 面会予約を辞退する
   */
  public async unconsented(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "辞退中...";

    this.showUnconsentedRegisterConfirmModal = false;
    this.showUnconsentedRegisterModal = false;

    const reservation: ReservationsGetModel = this.reservation as ReservationsGetModel;
    let requestBody: ReservationsDeleteModel | null = null;
    if (this.family && this.family.senderType == SenderType.LINE) {
      requestBody = this.createDeleteRequestBody(ReservationDeleteSendType.LINE, reservation.type);
    } else {
      requestBody = this.createDeleteRequestBody(ReservationDeleteSendType.MAIL, reservation.type);
    }
    await this.reservationsService.delete(userStore, reservation.id, requestBody);

    this.okModalTitle = "辞退しました。";
    this.showOkModal = true;
    this.isBusy = false;

    return;
  }

  public createDeleteRequestBody(sendType: ReservationDeleteSendType, reservationType: ReservationType): ReservationsDeleteModel {
    return {
      title: this.messageTitle,
      text: this.messageText,
      reservationType: reservationType,
      sendType: sendType
    };
  }

  /**
   * ### 承諾キャンセルモーダルを表示する
   *
   * - 家族IDが0(代理予約ではないか、登録済み家族の代理予約ではない)であればモーダルに追加メッセージを表示する
   */
  public async onConsentedCancel(reservation: ReservationsGetModel): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読み込み中...";

    // 代理予約の場合はメッセージモーダルではなく既存のモーダルを表示する
    if (reservation.familyId == 0 && reservation.inputType == InputType.FACILITY_APP) {
      this.modalMessage = `${reservation.residentName}（${reservation.residentRoom}）様の${reservation.familyName}（${reservation.familyRelation}）の面会予約をキャンセルしますか？`;
      this.modalOptionMessage = "登録されていないご家族の面会予約のため、キャンセル自動通知がありません。お電話でのご連絡をお願いします。";
      this.messageTitle = "";
      this.messageText = "";

      this.isBusy = false;
      this.showConsentedCancelRegisterModalByNoMessage = true;
    }
    // 代理予約以外の場合はメッセージモーダルを表示する
    else {
      const family: FamilyGetModel = await this.getFamily(reservation.residentId, reservation.familyId);
      this.family = family;
      this.setMessageModalTitleAndText(family, reservation, "キャンセル");

      this.isBusy = false;
      this.showConsentedCancelRegisterModal = true;
    }

    return;
  }

  /**
   * ### 承諾キャンセル確認ダイアログを表示する
   */
  public confirmConsentedCancel(): void {
    const reservation: ReservationsGetModel = this.reservation as ReservationsGetModel;
    this.messageConfirmModalTitle = `${reservation.residentName}（${reservation.residentRoom}）様の${reservation.familyName}（${reservation.familyRelation}）に以下の内容でメッセージを送信します。よろしいですか？`;
    this.showConsentedCancelRegisterConfirmModal = true;
    return;
  }

  /**
   * ### 面会予約をキャンセルする
   */
  public async consentedCancel(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "キャンセル中...";

    this.showConsentedCancelRegisterConfirmModal = false;
    this.showConsentedCancelRegisterModal = false;

    const reservation: ReservationsGetModel = this.reservation as ReservationsGetModel;
    let requestBody: ReservationsDeleteModel | null = null;
    if (reservation.familyId == 0 && reservation.inputType == InputType.FACILITY_APP) {
      requestBody = this.createDeleteRequestBody(ReservationDeleteSendType.NONE, reservation.type);
    } else if (this.family && this.family.senderType == SenderType.LINE) {
      requestBody = this.createDeleteRequestBody(ReservationDeleteSendType.LINE, reservation.type);
    } else {
      requestBody = this.createDeleteRequestBody(ReservationDeleteSendType.MAIL, reservation.type);
    }
    await this.reservationsService.delete(userStore, reservation.id, requestBody);

    this.okModalTitle = "キャンセルしました。";
    this.showOkModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### オンライン面会を開始する
   */
  public onOnlineUrl(reservation: ReservationsGetModel): void {
    window.open(reservation.onlineVisitUrl, "_blank");
    return;
  }

  /**
   * ### 戻るボタン押下イベント
   */
  public onBack(): void {
    const page: string = this.$route.params.page;
    this.$router.push({ name: MobileUrl.ReservationList, params: { page: page } });

    return;
  }

  /**
   * ### フッターを表示する
   */
  public showFooter(): void {
    this.$emit("updateShowFooter", true);
    return;
  }

  /**
   * ### フッターを非表示にする
   */
  public hideFooter(): void {
    this.$emit("updateShowFooter", false);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/mobile/_mixin.scss";
@import "../../../styles/mobile/_colors.scss";

#reservation-detail {
  .pen {
    @include svg-icon("../../../assets/pen.svg", $font-base, 16px, 16px);
  }
  .icon-offline {
    @include svg-icon("../../../assets/reservation/offline.svg", $font-base, 24px, 24px);
  }
  .icon-online {
    @include svg-icon("../../../assets/reservation/online.svg", $font-base, 24px, 24px);
  }

  .sunday {
    color: $font-sunday;
  }
  .saturday {
    color: $font-saturday;
  }

  .edit-text {
    color: $font-base;
    opacity: 0.5;
    text-shadow: 0 1px 0 #fff;
    cursor: pointer;
  }
  .edit-text:hover {
    opacity: 0.75;
  }
  .reservation-text {
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .status-consented {
    color: $font-disable;
    font-weight: bold;
  }
  .status-no-consented {
    color: $font-strong;
    font-weight: bold;
  }
}
</style>
